/**
 * PetiteEnfanceEspaceSections — sections « espace parent » de la petite enfance,
 * réutilisables : rendues à la fois par la page autonome /petite-enfance/espace
 * et par la brique famille unifiée /scolaire-cantine (vue administré).
 *
 * Une seule source de vérité : mes enfants accueillis, mes pièces (bibliothèque
 * famille centralisée), mes factures PE, mes demandes de place, annuaire assmat.
 *
 * Accent rose petite enfance partout (boutons + badges) — pas de bleu résiduel.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api } from '@/lib/api';
import type { PeEspaceParent, PeContratType } from '@/lib/api';
import { Baby, FileText, HandHeart, ClipboardList, Plus, Download, Clock } from 'lucide-react';

const ACCENT = 'oklch(0.55 0.20 25)'; // rouge enfance/cantine — côté administré tout est rouge

async function downloadBlob(getBlob: () => Promise<Blob>, filename: string) {
  const blob = await getBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export function PetiteEnfanceEspaceSections({ setError }: { setError: (s: string | null) => void; embedded?: boolean }) {
  const [data, setData] = useState<PeEspaceParent | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDemande, setShowDemande] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.petiteEnfance.espace.get()); }
    catch { setError('Impossible de charger votre espace petite enfance.'); }
    finally { setLoading(false); }
  }, [setError]);
  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <Card variant="glass" padded>
        <div className="py-8 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: ACCENT, borderTopColor: 'transparent' }} role="status" aria-label="Chargement" />
        </div>
      </Card>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5">
      {/* Mes enfants accueillis */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <Baby className="h-4 w-4" style={{ color: ACCENT }} aria-hidden="true" />
          <h2 className="font-display text-sm font-semibold text-ink">Mes enfants accueillis</h2>
        </div>
        {data.enfants.length === 0 ? (
          <Card variant="glass" padded><p className="py-4 text-center text-sm text-ink-subtle">Aucun enfant accueilli en petite enfance pour le moment.</p></Card>
        ) : (
          <div className="space-y-2">
            {data.enfants.map((e) => (
              <Card key={e.enfantId} variant="glass" className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink">{e.prenom} {e.nom}</div>
                  <div className="text-[11px] text-ink-subtle">{e.structureNom ?? 'Structure à définir'}{e.typeContrat ? ` · ${e.typeContrat}` : ''}</div>
                </div>
                {e.statutContrat && <Badge variant={e.statutContrat === 'actif' ? 'success' : 'neutral'} size="sm">{e.statutContrat}</Badge>}
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Mes factures petite enfance */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <FileText className="h-4 w-4" style={{ color: ACCENT }} aria-hidden="true" />
          <h2 className="font-display text-sm font-semibold text-ink">Mes factures — petite enfance</h2>
        </div>
        {data.factures.length === 0 ? (
          <Card variant="glass" padded><p className="py-4 text-center text-sm text-ink-subtle">Aucune facture.</p></Card>
        ) : (
          <div className="space-y-1.5">
            {data.factures.map((f) => (
              <Card key={f.id} variant="glass" className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-ink">{f.numero}</div>
                  <div className="text-[11px] text-ink-subtle">{new Date(f.periodeDebut + 'T00:00:00').toLocaleDateString('fr-FR')} → {new Date(f.periodeFin + 'T00:00:00').toLocaleDateString('fr-FR')}</div>
                </div>
                <Badge variant={f.statut === 'payee' ? 'success' : f.statut === 'impayee' ? 'urgent' : 'neutral'} size="sm">{f.statut}</Badge>
                <span className="text-sm font-semibold tabular-nums text-ink">{f.montantEuros.toFixed(2)} €</span>
                <button type="button" title="Télécharger (PDF)"
                  onClick={() => downloadBlob(() => api.petiteEnfance.factureBlobParent(f.id), `${f.numero}.pdf`).catch(() => setError('Téléchargement impossible.'))}
                  className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-r-md border border-border text-ink-muted transition hover:bg-surface hover:text-ink">
                  <Download className="h-4 w-4" aria-hidden="true" />
                </button>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Mes demandes de place (pré-inscriptions) */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" style={{ color: ACCENT }} aria-hidden="true" />
            <h2 className="font-display text-sm font-semibold text-ink">Mes demandes de place</h2>
          </div>
          <Button type="button" variant="cantine" size="sm" onClick={() => setShowDemande(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />Faire une demande
          </Button>
        </div>
        {data.preinscriptions.length === 0 ? (
          <Card variant="glass" padded><p className="py-4 text-center text-sm text-ink-subtle">Aucune demande en cours.</p></Card>
        ) : (
          <div className="space-y-1.5">
            {data.preinscriptions.map((p) => (
              <Card key={p.id} variant="glass" className="flex flex-col gap-1 p-3">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-ink">{p.enfantPrenom} {p.enfantNom}</span>
                    <div className="text-[11px] text-ink-subtle">Déposée le {new Date(p.dateDepot + 'T00:00:00').toLocaleDateString('fr-FR')}</div>
                  </div>
                  <Badge variant="cantine" size="sm">{p.statut}</Badge>
                </div>
                {p.motifAttribution && (
                  <div className="mt-1 rounded-r-md border border-cantine/25 bg-cantine-tint/20 px-2.5 py-1.5 text-[11px] text-ink">
                    <span className="font-medium">Message de la mairie :</span> {p.motifAttribution}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Annuaire des assistantes maternelles */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <HandHeart className="h-4 w-4" style={{ color: ACCENT }} aria-hidden="true" />
          <h2 className="font-display text-sm font-semibold text-ink">Assistantes maternelles disponibles</h2>
        </div>
        {data.annuaire.length === 0 ? (
          <Card variant="glass" padded><p className="py-4 text-center text-sm text-ink-subtle">Aucune disponibilité publiée actuellement.</p></Card>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {data.annuaire.map((a) => (
              <Card key={a.id} variant="glass" className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-ink">{a.prenom} {a.nom}</div>
                    <div className="text-[11px] text-ink-subtle">{a.commune ?? '—'}{a.telephone ? ` · ${a.telephone}` : ''}</div>
                  </div>
                  <Badge variant="success" size="sm">{a.placesDispo} place(s)</Badge>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Horaires d'ouverture des structures d'accueil */}
      {data.structures && data.structures.length > 0 && (
        <section>
          <div className="mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4" style={{ color: ACCENT }} aria-hidden="true" />
            <h2 className="font-display text-sm font-semibold text-ink">Horaires des structures d'accueil</h2>
          </div>
          <Card variant="glass" padded>
            <ul className="space-y-2">
              {data.structures.map((s) => (
                <li key={s.id} className="text-sm">
                  <span className="font-medium text-ink">{s.nom}</span>
                  {(s.amplitudeDebut || s.amplitudeFin) && (
                    <span className="ml-2 text-ink-subtle">
                      {s.amplitudeDebut && s.amplitudeFin ? `${s.amplitudeDebut} – ${s.amplitudeFin}` : (s.amplitudeDebut || s.amplitudeFin)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}

      {showDemande && <DemandeForm onClose={() => setShowDemande(false)} onSaved={async () => { setShowDemande(false); await load(); }} setError={setError} />}
    </div>
  );
}

function DemandeForm({ onClose, onSaved, setError }: { onClose: () => void; onSaved: () => Promise<void>; setError: (s: string | null) => void }) {
  const [f, setF] = useState({ enfantPrenom: '', enfantNom: '', dateNaissance: '', ne: false, typeSouhaite: 'regulier' as PeContratType, dateAccueilSouhaitee: '', heuresSouhaitees: '', commentaire: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.enfantPrenom.trim() && !f.enfantNom.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.petiteEnfance.espace.deposerPreinscription({
        enfantPrenom: f.enfantPrenom || null, enfantNom: f.enfantNom || null,
        dateNaissance: f.dateNaissance || null, ne: f.ne, typeSouhaite: f.typeSouhaite,
        dateAccueilSouhaitee: f.dateAccueilSouhaitee || null, commentaire: f.commentaire || null,
        heuresSouhaitees: f.heuresSouhaitees ? parseFloat(f.heuresSouhaitees.replace(',', '.')) || 0 : 0,
      });
      await onSaved();
    } catch { setError('Envoi impossible.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} title="Demande de place en accueil" size="md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input value={f.enfantPrenom} onChange={(e) => setF({ ...f, enfantPrenom: e.target.value })} placeholder="Prénom de l'enfant" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <input value={f.enfantNom} onChange={(e) => setF({ ...f, enfantNom: e.target.value })} placeholder="Nom de l'enfant" className="rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="de-dn">Naissance (réelle ou prévue)</label>
            <input id="de-dn" type="date" value={f.dateNaissance} onChange={(e) => setF({ ...f, dateNaissance: e.target.value })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm text-ink">
            <input type="checkbox" checked={f.ne} onChange={(e) => setF({ ...f, ne: e.target.checked })} className="h-4 w-4" /> Enfant déjà né
          </label>
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="de-type">Type d'accueil souhaité</label>
            <select id="de-type" value={f.typeSouhaite} onChange={(e) => setF({ ...f, typeSouhaite: e.target.value as PeContratType })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none">
              <option value="regulier">Régulier</option>
              <option value="occasionnel">Occasionnel</option>
              <option value="urgence">Urgence</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="de-da">Accueil souhaité dès le</label>
            <input id="de-da" type="date" value={f.dateAccueilSouhaitee} onChange={(e) => setF({ ...f, dateAccueilSouhaitee: e.target.value })} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-2 py-2 text-sm text-ink focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] text-ink-subtle" htmlFor="de-h">Heures/mois souhaitées (optionnel)</label>
            <input id="de-h" value={f.heuresSouhaitees} onChange={(e) => setF({ ...f, heuresSouhaitees: e.target.value })} inputMode="decimal" placeholder="ex. 140" className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
          </div>
        </div>
        <textarea value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} placeholder="Précisions (optionnel)" rows={2} className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:outline-none" />
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
          <Button type="button" variant="cantine" size="sm" onClick={() => void submit()} disabled={saving}>{saving ? 'Envoi…' : 'Envoyer ma demande'}</Button>
        </div>
      </div>
    </Dialog>
  );
}
